import {
  DataGrid,
  type GridRowSelectionModel,
  type GridRowId,
} from '@mui/x-data-grid'
import { styled } from '@mui/system'
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControlLabel,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material'
import {
  DeleteOutlined as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material'
import AddIcon from 'components/add-icon'
import SearchField from 'components/search-field/index'
import colors from 'common/colors'
import { useTranslation } from 'react-i18next'
import { useEffect, useState, useCallback } from 'react'
import { trackEvent } from 'api/usage-tracking'

import type { AppState } from 'state/index'
import type { TenantUser } from 'state/account'
import { useSelector, useDispatch } from 'react-redux'
import { FT_ADMIN_CLAIM } from 'common/c'
import {
  fetchTenantUsers,
  createTenantUser,
  editTenantUser,
  deleteTenantUser,
} from 'api/user-management'
import { TEST_IDS } from 'test-ids'

type AvailableUserRole =
  | 'tenant-admin'
  | 'tenant-user'
  | 'tenant-operator'
  | 'farmtrx-admin'
  | 'partner-admin'
  | 'agpro'

export default function UserAccountsComponent() {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const tenantUsers = useSelector(
    (state) => (state as AppState).account.tenantUsers,
  )
  const permissions = useSelector(
    (state) => (state as AppState).account.permissions,
  )
  const currentUser = useSelector((state) => (state as AppState).account.user)
  const customClaims = useSelector(
    (state) => (state as AppState).account.customClaims,
  )

  // Compute available roles based on permissions and current user role
  const isFtAdmin = permissions.includes('farmtrx:admin')
  const isPartnerAdmin = permissions.includes('partner:admin')
  const isAgproUser = (currentUser as any)?.role === 'agpro'
  // Visiting scenario: farmtrx-admin visiting a tenant via M2M token
  const isVisitingAsFtAdmin = customClaims?.[FT_ADMIN_CLAIM] === true
  // GET /user always re-fetches with the current JWT tid, so isPartnerTenant reflects the visited tenant
  const isVisitingPartnerTenant =
    isVisitingAsFtAdmin && (currentUser as any)?.isPartnerTenant === true

  const availableRoles: Set<AvailableUserRole> = new Set([
    'tenant-admin',
    'tenant-user',
    'tenant-operator',
  ])
  // partner-admin: available when visiting a partner tenant as a farmtrx-admin,
  // or when a home partner-admin manages their own tenant.
  // Not available to ft-admin in the FARMTRX home tenant (no custom claims).
  if (isPartnerAdmin && !isFtAdmin) {
    availableRoles.add('partner-admin')
  }
  if (isVisitingPartnerTenant) {
    availableRoles.add('partner-admin')
  }
  if (isFtAdmin || isAgproUser || isVisitingAsFtAdmin) {
    availableRoles.add('agpro')
  }
  if (isFtAdmin) {
    availableRoles.add('farmtrx-admin')
  }

  const [searchValue, setSearchValue] = useState('')
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>({
      type: 'include',
      ids: new Set<GridRowId>([]),
    })
  const [loading, setLoading] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add')
  const [selectedUser, setSelectedUser] = useState<TenantUser | null>(null)
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    role: 'tenant-user' as AvailableUserRole,
  })
  const [formErrors, setFormErrors] = useState<{
    [key: string]: string
  }>({})

  useEffect(() => {
    dispatch(fetchTenantUsers() as any)
  }, [dispatch])

  // Agpro users can only manage users in their home tenant, not when visiting
  const isVisitingAsAgproUser =
    isAgproUser && customClaims && Object.keys(customClaims).length > 0
  const canManage =
    permissions.includes('users:write') && !isVisitingAsAgproUser

  const filteredUsers = tenantUsers.filter((user) => {
    if (!searchValue) return true
    const lower = searchValue?.toLowerCase() ?? ''
    return (
      user.fullName?.toLowerCase().includes(lower) ||
      user.email?.toLowerCase().includes(lower) ||
      user.role?.toLowerCase().includes(lower)
    )
  })

  const handleAddClick = useCallback(() => {
    setDialogMode('add')
    setSelectedUser(null)
    setFormData({
      fullName: '',
      email: '',
      role: 'tenant-user',
    })
    setFormErrors({})
    setDialogOpen(true)
  }, [])

  const handleEditClick = useCallback(() => {
    if (rowSelectionModel.ids.size === 1) {
      const user = tenantUsers.find(
        (u) => u.id === rowSelectionModel.ids.values().next().value,
      )
      if (user) {
        setDialogMode('edit')
        setSelectedUser(user)
        const knownRoles: AvailableUserRole[] = [
          'tenant-admin',
          'tenant-user',
          'tenant-operator',
          'farmtrx-admin',
          'partner-admin',
          'agpro',
        ]
        setFormData({
          fullName: user.fullName ?? '',
          email: user.email ?? '',
          role: knownRoles.includes(user.role as AvailableUserRole)
            ? (user.role as AvailableUserRole)
            : 'tenant-user',
        })
        setFormErrors({})
        setDialogOpen(true)
      }
    }
  }, [rowSelectionModel, tenantUsers])

  const handleDeleteClick = useCallback(() => {
    if (rowSelectionModel.ids.size === 1) {
      const user = tenantUsers.find(
        (u) => u.id === rowSelectionModel.ids.values().next().value,
      )
      if (user) {
        setSelectedUser(user)
        setDeleteDialogOpen(true)
      }
    }
  }, [rowSelectionModel, tenantUsers])

  const isDeleteDisabled = () => {
    if (rowSelectionModel.ids.size !== 1) return true
    if (tenantUsers.length === 1) return true
    const selectedUser = tenantUsers.find(
      (u) => u.id === rowSelectionModel.ids.values().next().value,
    )
    const currentUserId = (currentUser as any)?.id
    return selectedUser?.id === currentUserId
  }

  const getDeleteButtonTitle = () => {
    if (rowSelectionModel.ids.size !== 1)
      return t('delete_item', { item: t('user') })
    if (tenantUsers.length === 1) return t('cannot_delete_last_user')
    const selectedUser = tenantUsers.find(
      (u) => u.id === rowSelectionModel.ids.values().next().value,
    )
    const currentUserId = (currentUser as any)?.id
    if (selectedUser?.id === currentUserId)
      return t('cannot_delete_own_account')
    return t('delete_item', { item: t('user') })
  }

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {}

    if (!formData.fullName.trim()) {
      errors.fullName = t('name_required')
    }
    if (!formData.email.trim()) {
      errors.email = t('email_required')
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = t('invalid_email_format')
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false)
    setFormData({
      fullName: '',
      email: '',
      role: 'tenant-user',
    })
    setFormErrors({})
    setSelectedUser(null)
  }, [])

  const roleChanged =
    dialogMode === 'edit' && selectedUser && selectedUser.role !== formData.role

  const handleDialogSubmit = useCallback(async () => {
    if (!validateForm()) return

    setLoading(true)
    try {
      if (dialogMode === 'add') {
        // Build payload based on role
        const payload: any = {
          fullName: formData.fullName,
          email: formData.email,
          role: formData.role,
        }

        await dispatch(createTenantUser(payload) as any)
        trackEvent('user_created', { role: formData.role })
      } else if (selectedUser) {
        await dispatch(
          editTenantUser(selectedUser.id, {
            fullName: formData.fullName,
            email: formData.email,
            role: formData.role,
          }) as any,
        )
        trackEvent('user_updated', {
          role: formData.role,
          roleChanged,
        })
      }
      handleDialogClose()
      setRowSelectionModel({ type: 'include', ids: new Set<GridRowId>([]) })
    } catch (error) {
      console.error('Error saving user:', error)
    } finally {
      setLoading(false)
    }
  }, [
    dialogMode,
    selectedUser,
    formData,
    dispatch,
    handleDialogClose,
    roleChanged,
  ])

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedUser) return

    setLoading(true)
    try {
      await dispatch(deleteTenantUser(selectedUser.id) as any)
      trackEvent('user_deleted', { role: selectedUser.role })
      setDeleteDialogOpen(false)
      setSelectedUser(null)
      setRowSelectionModel({ type: 'include', ids: new Set<GridRowId>([]) })
    } catch (error) {
      console.error('Error deleting user:', error)
    } finally {
      setLoading(false)
    }
  }, [selectedUser, dispatch])

  const columns = [
    {
      field: 'fullName',
      headerName: t('name'),
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'email',
      headerName: t('email'),
      flex: 1,
      minWidth: 250,
    },
    {
      field: 'role',
      headerName: t('user_role'),
      flex: 1,
      minWidth: 150,
      renderCell: (params: any) => {
        const roleValue = params.value || ''
        const roleKey = `role_${roleValue.replace(/-/g, '_')}`
        return roleValue ? t(roleKey) : ''
      },
    },
  ]

  return (
    <Box
      sx={{
        width: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: '1rem',
        height: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h1>{t('user_accounts')}</h1>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {canManage && (
            <>
              <Tooltip title={getDeleteButtonTitle()}>
                <span>
                  <IconButton
                    data-testid={TEST_IDS.deleteUserButton}
                    disabled={isDeleteDisabled()}
                    onClick={handleDeleteClick}
                    size="large"
                  >
                    <DeleteIcon
                      style={{
                        color: !isDeleteDisabled() ? colors.brand : 'gray',
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
              <IconButton
                data-testid={TEST_IDS.editUserButton}
                title={t('edit_user')}
                disabled={rowSelectionModel?.ids?.size !== 1}
                onClick={handleEditClick}
                size="large"
              >
                <EditIcon
                  style={{
                    color:
                      rowSelectionModel?.ids?.size === 1
                        ? colors.brand
                        : 'gray',
                  }}
                />
              </IconButton>

              <IconButton
                data-testid={TEST_IDS.addUserButton}
                onClick={handleAddClick}
                title={t('add_user')}
                size="large"
              >
                <AddIcon />
              </IconButton>
            </>
          )}
          <SearchField
            sx={{ marginLeft: '10px', minWidth: '200px' }}
            name={searchValue}
            placeholder={t('search')}
            className=""
            onSearch={(value: string) => setSearchValue(value)}
          />
        </Box>
      </Box>
      <Box
        sx={{ flexGrow: 1, height: '100%', width: '100%', marginTop: '2rem' }}
      >
        <DataGrid
          data-testid={TEST_IDS.userManagementTable}
          rows={filteredUsers.map((u) => ({
            ...u,
            id: u.id,
          }))}
          columns={columns}
          checkboxSelection={canManage}
          disableMultipleRowSelection={true}
          onRowSelectionModelChange={(newSelection: any) =>
            setRowSelectionModel(newSelection)
          }
          rowSelectionModel={rowSelectionModel}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10, page: 0 } },
            sorting: { sortModel: [{ field: 'fullName', sort: 'asc' }] },
          }}
          sx={{
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: colors.greyBackground,
              textTransform: 'uppercase',
            },
          }}
        />
      </Box>
      {/* Add/Edit User Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        data-testid={TEST_IDS.userDialog}
      >
        <DialogTitle>
          {dialogMode === 'add' ? t('add_user') : t('edit_user')}
        </DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}
        >
          {roleChanged && (
            <Alert
              severity="warning"
              data-testid={TEST_IDS.userRoleChangeWarning}
            >
              {t('role_change_warning')}
            </Alert>
          )}
          <StyledForm>
            <FormControlLabel
              control={
                <TextField
                  value={formData.fullName}
                  onChange={(e) =>
                    setFormData({ ...formData, fullName: e.target.value })
                  }
                  error={!!formErrors.fullName}
                  helperText={formErrors.fullName}
                  data-testid={TEST_IDS.userNameInput}
                  sx={{ marginLeft: '10px' }}
                />
              }
              label={t('name')}
              labelPlacement="start"
            />
            <FormControlLabel
              control={
                <TextField
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                  data-testid={TEST_IDS.userEmailInput}
                  sx={{ marginLeft: '10px' }}
                />
              }
              label={t('email')}
              labelPlacement="start"
            />
            <FormControlLabel
              control={
                <Select
                  autoWidth
                  value={formData.role}
                  disabled={
                    dialogMode === 'edit' &&
                    selectedUser?.id === (currentUser as any)?.id
                  }
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      role: e.target.value as AvailableUserRole,
                    })
                  }
                  data-testid={TEST_IDS.userRoleSelect}
                >
                  {Array.from(availableRoles).map((role) => (
                    <MenuItem key={role} value={role}>
                      {t(`role_${role.replace(/-/g, '_')}`)}
                    </MenuItem>
                  ))}
                </Select>
              }
              label={t('user_role')}
              labelPlacement="start"
            />
          </StyledForm>
        </DialogContent>
        <DialogActions>
          <Button
            data-testid={TEST_IDS.formCancel}
            onClick={handleDialogClose}
            disabled={loading}
          >
            {t('cancel')}
          </Button>
          <Button
            data-testid={TEST_IDS.formSubmit}
            onClick={handleDialogSubmit}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading && <CircularProgress size={20} sx={{ mr: 1 }} />}
            {dialogMode === 'add' ? t('create') : t('save')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        data-testid={TEST_IDS.deleteUserDialog}
      >
        <DialogTitle>{t('delete_user')}</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ mt: 2 }}>
              <p>
                {t('confirm_delete_user')}{' '}
                <strong>{selectedUser.fullName}</strong> ({selectedUser.email})?
              </p>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            data-testid={TEST_IDS.formCancel}
            onClick={() => setDeleteDialogOpen(false)}
            disabled={loading}
          >
            {t('cancel')}
          </Button>
          <Button
            data-testid={TEST_IDS.formSubmit}
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading && <CircularProgress size={20} sx={{ mr: 1 }} />}
            {t('delete_user')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

const StyledForm = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  '& > *:not(:last-child)': {
    marginBottom: '1em',
  },
})
